import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import DataTable, { Column } from "../components/DataTable";

const LIMIT = 50;

interface AuditLog {
  id: string;
  operator_id: string | null;
  action: string;
  tenant_id: string | null;
  target_type: string;
  target_id: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

export default function AuditLogs() {
  const { t } = useTranslation();
  const [tenantId, setTenantId] = useState("");
  const [targetType, setTargetType] = useState("");
  const [action, setAction] = useState("");
  const [offset, setOffset] = useState(0);

  const { data: logs, isLoading, error } = useQuery({
    queryKey: ["ops-audit-logs", tenantId, targetType, action, offset],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
      if (tenantId) params.set("tenant_id", tenantId);
      if (targetType) params.set("target_type", targetType);
      if (action) params.set("action", action);
      const res = await api.get<AuditLog[]>(`/ops/audit-logs?${params.toString()}`);
      return res.data;
    },
  });

  const columns: Column<AuditLog>[] = [
    { key: "created", header: "Čas", render: (r) => new Date(r.created_at).toLocaleString("cs-CZ") },
    { key: "action", header: t("auditLogs.action"), render: (r) => r.action },
    { key: "target", header: t("auditLogs.targetType"), render: (r) => `${r.target_type} / ${r.target_id}` },
    { key: "tenant", header: t("auditLogs.tenantId"), render: (r) => r.tenant_id ?? "—" },
    { key: "ip", header: "IP", render: (r) => r.ip_address ?? "—" },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t("nav.auditLogs")}</h1>
        <p>{t("auditLogs.subtitle")}</p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="grid grid-3">
          <div>
            <label>{t("auditLogs.tenantId")}</label>
            <input value={tenantId} onChange={(e) => { setTenantId(e.target.value); setOffset(0); }} placeholder="UUID" />
          </div>
          <div>
            <label>{t("auditLogs.targetType")}</label>
            <input value={targetType} onChange={(e) => { setTargetType(e.target.value); setOffset(0); }} />
          </div>
          <div>
            <label>{t("auditLogs.action")}</label>
            <input value={action} onChange={(e) => { setAction(e.target.value); setOffset(0); }} />
          </div>
        </div>
      </div>

      <div className="card">
        <DataTable
          rows={logs ?? []}
          columns={columns}
          loading={isLoading}
          error={error ? t("auditLogs.loadError") : null}
          emptyMessage={t("auditLogs.noLogs")}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} disabled={offset === 0}>{t("common.previous")}</button>
        <button onClick={() => setOffset((o) => o + LIMIT)} disabled={(logs ?? []).length < LIMIT}>{t("common.next")}</button>
      </div>
    </div>
  );
}
